import { Button, Flex, HStack, SimpleGrid, Text, useColorModeValue, useToast } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { MdOutlineLink } from 'react-icons/md';
import { Billet, Charge, CreditCardInfo, ItemChargeStatus, Pix } from 'types/Charge';
import { maskCurrency } from 'utils/number';
import CopyableText from 'components/CopyableText';
import { DefinitionRow, InfoPanel } from 'components/DetailSection';
import { BilletPixStatusRender, ChargeStatus, CreditCardStatusRender } from '.';
import SeeQrCode from './SeeQrCode';

const formatDate = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss') : '---';

const PixPanel: FC<{ pix: Pix; chargeAmount: string }> = ({ pix, chargeAmount }) => {
  const toast = useToast();

  return (
    <InfoPanel title="Pix" action={BilletPixStatusRender(pix.status)}>
      <DefinitionRow label="Valor">{maskCurrency(pix.amount)}</DefinitionRow>
      <DefinitionRow label="Criado em">{formatDate(pix.createdAt)}</DefinitionRow>
      <DefinitionRow label="TxId">
        <CopyableText value={pix.referenceIntegrationId} label="TxId" />
      </DefinitionRow>
      <DefinitionRow label="EndToEndId">
        <CopyableText value={pix.endToEndId} label="EndToEndId" />
      </DefinitionRow>
      <DefinitionRow label="ID">
        <CopyableText value={pix.id} label="ID do Pix" />
      </DefinitionRow>

      {pix.status === ItemChargeStatus.PENDING && pix.qrCode && (
        <HStack mt={3} spacing={2} translate="no">
          <Button
            size="sm"
            colorScheme="brand"
            variant="outline"
            leftIcon={<MdOutlineLink />}
            onClick={() => {
              navigator.clipboard.writeText(pix.qrCode);
              toast({
                title: 'Sucesso',
                description: 'Código copiado com sucesso',
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }}
          >
            Copiar código
          </Button>
          <SeeQrCode data={{ qrCode: pix.qrCode, amount: +chargeAmount }} />
        </HStack>
      )}
    </InfoPanel>
  );
};

const CreditCardPanel: FC<{ creditCard: CreditCardInfo }> = ({ creditCard }) => (
  <InfoPanel title="Cartão de crédito" action={CreditCardStatusRender(creditCard.status)}>
    <DefinitionRow label="Valor">{maskCurrency(creditCard.amount)}</DefinitionRow>
    {+creditCard.amountRefunded > 0 && (
      <DefinitionRow label="Devolvido">{maskCurrency(creditCard.amountRefunded)}</DefinitionRow>
    )}
    <DefinitionRow label="Criado em">{formatDate(creditCard.createdAt)}</DefinitionRow>
    <DefinitionRow label="Cartão">{creditCard.creditCard || '---'}</DefinitionRow>
    <DefinitionRow label="Bandeira">{creditCard.brand || '---'}</DefinitionRow>
    <DefinitionRow label="Parcelas">{creditCard.installments ?? '---'}</DefinitionRow>
    <DefinitionRow label="Autorização">{creditCard.authorizationCode || '---'}</DefinitionRow>
    <DefinitionRow label="NSU">{creditCard.nsu || '---'}</DefinitionRow>
    <DefinitionRow label="Provedor">{creditCard.type || '---'}</DefinitionRow>
    <DefinitionRow label="Id adquirente">
      <CopyableText value={creditCard.paymentId} label="Id adquirente" />
    </DefinitionRow>
    {creditCard.returnMessage && <DefinitionRow label="Mensagem da API">{creditCard.returnMessage}</DefinitionRow>}
  </InfoPanel>
);

const BilletPanel: FC<{ billet: Billet }> = ({ billet }) => {
  const toast = useToast();

  return (
    <InfoPanel title="Boleto" action={BilletPixStatusRender(billet.status)}>
      <DefinitionRow label="Criado em">{formatDate(billet.createdAt)}</DefinitionRow>
      <DefinitionRow label="Seu número">{billet.billetId || '---'}</DefinitionRow>
      <DefinitionRow label="Código">{billet.billetCode || '---'}</DefinitionRow>
      <DefinitionRow label="Código de barras">
        <CopyableText value={billet.billetDigit} label="código de barras" />
      </DefinitionRow>

      <Button
        mt={3}
        size="sm"
        colorScheme="brand"
        variant="outline"
        translate="no"
        leftIcon={<MdOutlineLink />}
        onClick={() => {
          navigator.clipboard.writeText(billet.billetDigit);
          toast({
            title: 'Sucesso',
            description: 'Boleto copiado com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }}
      >
        Copiar boleto
      </Button>
    </InfoPanel>
  );
};

/**
 * Os meios de pagamento da cobrança.
 *
 * Antes eram três painéis fixos, e os dois que não existiam ocupavam metade da tela dizendo
 * "não há informações". Agora só aparece o que existe; o que falta vira uma linha de ação no
 * rodapé, e só quando gerar aquele instrumento é de fato possível.
 */
const ChargePayments: FC<{
  data: Charge;
  onGenerate: (type: 'pix' | 'billet') => void;
}> = ({ data, onGenerate }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  const pixes = data?.Pix ?? [];
  const cards = data?.CreditCardPayment ?? [];
  const billets = data?.Billet ?? [];
  // Condições preservadas do código anterior: o Pix podia ser gerado a qualquer momento, o
  // boleto só com a cobrança pendente. A assimetria é antiga e não é assunto desta mudança.
  const canGeneratePix = !pixes.length;
  const canGenerateBillet = !billets.length && data?.status === ChargeStatus.PENDING;
  const hasAny = pixes.length + cards.length + billets.length > 0;

  return (
    <>
      {hasAny && (
        <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={4}>
          {pixes.map((pix) => (
            <PixPanel key={pix.id} pix={pix} chargeAmount={data.amount} />
          ))}
          {cards.map((creditCard) => (
            <CreditCardPanel key={creditCard.id} creditCard={creditCard} />
          ))}
          {billets.map((billet) => (
            <BilletPanel key={billet.id} billet={billet} />
          ))}
        </SimpleGrid>
      )}

      {!hasAny && !canGeneratePix && !canGenerateBillet && (
        <Text fontSize="sm" color={muted}>
          Nenhum meio de pagamento foi gerado para esta cobrança.
        </Text>
      )}

      {(canGeneratePix || canGenerateBillet) && (
        <Flex align="center" gap={3} mt={hasAny ? 4 : 0} flexWrap="wrap">
          <Text fontSize="sm" color={muted}>
            {hasAny ? 'Gerar outro meio de pagamento:' : 'Nenhum meio de pagamento gerado ainda.'}
          </Text>
          {canGeneratePix && (
            <Button size="sm" colorScheme="brand" variant="outline" onClick={() => onGenerate('pix')}>
              Gerar Pix
            </Button>
          )}
          {canGenerateBillet && (
            <Button size="sm" colorScheme="brand" variant="outline" onClick={() => onGenerate('billet')}>
              Gerar boleto
            </Button>
          )}
        </Flex>
      )}
    </>
  );
};

export default ChargePayments;
